import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { emptyModal } from 'src/app/modal/DAO';
import { Endorsement, EndorsementQuestionGroup, EndorsementTypes, PortalStatus } from 'src/app/modal/Endorsements/Endorsement.Model';
import { IBasePage } from 'src/app/modal/IBasePage.modules';
import { Constants } from 'src/app/modal/ProjConstants.models';
import { QuestionAnswer, Questionnaire } from 'src/app/modal/questionnaire.models';
import { MenuClickContext } from 'src/app/modal/routing.models';
import { CommonService } from 'src/app/services/commonService';
import { EndorsementService } from 'src/app/services/endorsement.service';
import { Subscription } from 'rxjs';
import { FileUploadFormComponent } from '../file-upload-form/file-upload-form.component';

@Component({
  selector: 'other-endorsements',
  templateUrl: './otherEndorsements.component.html'
})
export class OtherEndorsementsComponent implements OnInit, OnDestroy, IBasePage {

  @ViewChild('fileUploadForm') fileUploadForm: FileUploadFormComponent;
  public otherEndorsementsForm: UntypedFormGroup;
  private endorsementQuestionGroup: EndorsementQuestionGroup;
  private menuClickSubscription: Subscription;
  private readonly toastValidation: string = 'Please fill the required fields highlighted in red';
  public fileNetAllowedFileExtensions = Constants.FileNetAllowedFileExtensions.toString();
  public isSubmitted: boolean = false;
  public endorsement: Endorsement = new Endorsement();
  public otherEndorsement: OtherEndorsement = new OtherEndorsement();
  private menuClickContext: MenuClickContext;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private messageService: MessageService,
    private commonService: CommonService,
    public router: Router,
    private endorsementService: EndorsementService
  ) {
     this.endorsement =  this.endorsementService.currentEndorsement;
  }

  ngOnInit(): void {
    this.initializeData();
    this.menuClickSubscription = this.endorsementService.menuClickBehavior.subscribe(
      (menuClickContext: MenuClickContext) => { this.menuClicked(menuClickContext) }
    );
  }

  private async getQuestionAnswers(): Promise<Questionnaire> {
    let payload = { "urlData": [this.endorsement.endorsementId, EndorsementTypes.otherEndorsements] };
    return this.commonService.get('endorsement', 'getQuestionAnswers', payload).toPromise()
      .then(questionAnswer => {
        const questionnaire: Questionnaire = new Questionnaire();
        questionnaire.key = EndorsementTypes.otherEndorsements;
        questionnaire.questionAnswer = questionAnswer
        return questionnaire;
      })
      .catch(error => Promise.reject(error));
  }

  public async initializeData(): Promise<void> {
    await this.endorsementService.reloadQuestionGroups(this.endorsement);
    this.endorsementQuestionGroup = this.endorsement.selectedEndorsementGroups.find(x => x.groupKey == EndorsementTypes.otherEndorsements);
    this.getQuestionAnswers().then(async questionnaire => {
      if (!!questionnaire.questionAnswer && questionnaire.questionAnswer.length > 0) {
        this.endorsementQuestionGroup.questionAnswers = questionnaire.questionAnswer;
        this.otherEndorsement.instantiateFrom(questionnaire);
        this.buildReactiveForm();
      }
    }).catch(error => {
      console.error(error);
    });
  }

  private buildReactiveForm(): void {
    if (!!!this.otherEndorsement) return;

    this.otherEndorsementsForm = this.formBuilder.group({
      notes: [this.otherEndorsement.notes.answer, this.otherEndorsement.notes.required ? Validators.required : null]
    });
  }

  public menuClicked(menuClickContext: MenuClickContext): void {
    this.menuClickContext = menuClickContext;
    if (menuClickContext != null && menuClickContext.page != EndorsementTypes.otherEndorsements) {
      if (!this.otherEndorsementsForm) {
        this.menuClickContext.page = EndorsementTypes.otherEndorsements;
        return;
      }
      menuClickContext.currentPageIsValid = this.isPageValid();
      menuClickContext.currentPageDataChanged = this.isPageDataChanged();
      if (this.endorsementService.isMenuClickHeaderPage(menuClickContext)) {
        this.menuClickContext.page = EndorsementTypes.otherEndorsements;
        return;
      }
      if (menuClickContext.currentPageIsValid && menuClickContext.currentPageDataChanged) {
        this.saveAndContinue();
      }
      this.endorsementService.handleUnsavedChangesOrNavigateToUrl(menuClickContext, this.endorsement);
    }
  }

  public isPageDataChanged(): boolean {
    return this.otherEndorsementsForm.touched || this.fileUploadForm?.isFormDataChanged();
  }
  public isPageValid(): boolean {
    return this.otherEndorsementsForm.valid;
  }

  public onRollback(currentTempage: string): void { }

  public onBack(page: string): void { }

  public saveAndContinue(type = ""): void {
    this.isSubmitted = true;
    if (this.isPageValid()) {
      this.postQuestionAnswers().then(isSuccess => {
        if (isSuccess) {
          this.isSubmitted = false;
          let currrentEndorsementQuestionGroup : EndorsementQuestionGroup = this.endorsement.selectedEndorsementGroups.find(x => x.groupKey == EndorsementTypes.otherEndorsements);
          currrentEndorsementQuestionGroup.hasAnswers = true;
          currrentEndorsementQuestionGroup.isValidPage = true;
          this.endorsement.userIsAt = '';
          if (type === "finalSubmit") {
              this.endorsementService.updateStatusToSubmitted(this.endorsement.endorsementId);
          }
          else if (type === "draft") {
            this.router.navigate(['/home'], { state: { data: 'endorsement' } });
          }
        }
      }).catch(error => {
        console.error(error);
        this.messageService.add({ key: 'toast', severity: 'error', summary: 'Other Endorsements', detail: error });
      })
    }
    else
      this.messageService.add({ key: 'toast', severity: 'error', summary: 'Other Endorsements', detail: this.toastValidation });
  }

  private async postQuestionAnswers(): Promise<boolean> {
    this.bindAnswersToQuestions();
    let payload = { "payloadData": this.endorsementQuestionGroup }
    return this.commonService.post('endorsement', 'postQuestionAnswers', payload).toPromise()
      .then(result => result)
      .catch(error => Promise.reject(error));
  }

  public bindAnswersToQuestions(): void {
    this.otherEndorsement.notes.answer = this.otherEndorsementsForm.get('notes')?.value;
    let questionAnswers: QuestionAnswer[] = [this.otherEndorsement.notes];
    questionAnswers.push(...this.fileUploadForm.convertFileDocumentsToQuestionAnswer());
    this.endorsementQuestionGroup.questionAnswers = questionAnswers;
  }
  public onReject(type: string): void {
    this.messageService.clear(type);
    document.body.style.pointerEvents = "visible";
  }

  ngOnDestroy(): void {
    if (!!this.menuClickSubscription) {
      this.menuClickSubscription.unsubscribe();
    }
  }
}

class OtherEndorsement {
  public notes: emptyModal = new emptyModal();

  public instantiateFrom(questionnaire: Questionnaire): void {
    this.notes = emptyModal.initializeFrom(questionnaire.findQuestion('notes'));
  }
}
